/**
 * UpdateAvailablePill — the gold pill that appears at the bottom of
 * the screen when the deployed bundle is newer than the one running
 * in this tab. User clicks → reload, on their own schedule.
 *
 * Cadence:
 *   - First check 30s after mount (gives the page time to settle).
 *   - Every 5 min after that.
 *   - On every visibilitychange that returns the tab to visible
 *     (so people who tab back after lunch get checked immediately).
 *
 * Doesn't poll while the tab is hidden — pointless, and the
 * visibilitychange handler will catch any new deploys when they
 * return.
 */
import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { isStale } from "../utils/versionCheck";

const GOLD = "#E2DD9F";
const POLL_MS = 5 * 60 * 1000; // 5 minutes

export default function UpdateAvailablePill() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let interval = null;
    let firstCheckTimer = null;

    const check = async () => {
      if (cancelled || show) return;
      // Don't poll while the tab is hidden — wastes a fetch and the
      // visibilitychange handler covers the catch-up.
      if (document.visibilityState !== "visible") return;
      const stale = await isStale();
      if (!cancelled && stale) setShow(true);
    };

    // First check after a brief settle window. Avoids racing the
    // initial page paint and making it look like we're "loading" the
    // version banner during boot.
    firstCheckTimer = setTimeout(check, 30000);

    // Periodic check while tab is open.
    interval = setInterval(check, POLL_MS);

    // Tab returns → check immediately. This is the high-leverage
    // case: coach opens the tab in the morning, walks away,
    // comes back at lunch, we deployed twice in between.
    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      if (firstCheckTimer) clearTimeout(firstCheckTimer);
      if (interval) clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [show]);

  if (!show) return null;

  // Let autosave surfaces (e.g. the player plan) flush pending debounced writes
  // before we reload, so "new version" never drops the last edit. Surfaces that
  // save as you type listen for bam:flush-before-reload and write immediately;
  // the short wait gives those writes time to land.
  const reload = async () => {
    try { window.dispatchEvent(new Event("bam:flush-before-reload")); } catch {}
    await new Promise((r) => setTimeout(r, 500));
    try { window.location.reload(); } catch {}
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={reload}
      onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
        zIndex: 99998, // below NativeSplash (999999), above everything else
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 18px",
        borderRadius: 999,
        background: GOLD,
        color: "#111",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.2,
        cursor: "pointer",
        boxShadow: "0 8px 28px rgba(226,221,159,0.35), 0 0 0 1px rgba(0,0,0,0.04)",
        animation: "bamPillSlideUp .22s cubic-bezier(.2,.8,.2,1) both",
      }}
    >
      <Sparkles size={14}/> New version available · Reload
      <style>{`
        @keyframes bamPillSlideUp {
          from { transform: translate(-50%, 20px); opacity: 0; }
          to   { transform: translate(-50%, 0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
